import type { ReactNode } from "react"
import { Head } from "@inertiajs/react"
import { Container, Section, Heading, Text, Flex, Box } from "@radix-ui/themes"
import { ExternalLink, Hash, Instagram, MapPin, Navigation } from "lucide-react"
import Breadcrumb from "@/shared/Breadcrumb"
import LikeButton from "@/shared/likes/LikeButton"
import SponsorSlot from "@/shared/sponsors/SponsorSlot"
import { SPONSOR_DESKTOP_ONLY, SPONSOR_MOBILE_ONLY } from "@/shared/sponsors/visibility"
import EventFeed from "@/shared/events/EventFeed"
import CarBrowser from "@/shared/cars/CarBrowser"
import MediaBrowser from "@/shared/media/MediaBrowser"
import type { TracksShowProps, TracksShowTrack } from "@/types/tracks_show"

function RichSection({ title, html }: { title: string; html: string }) {
  return (
    <Box>
      <Heading size="4" mb="2">{title}</Heading>
      <div className="trix-content" dangerouslySetInnerHTML={{ __html: html }} />
    </Box>
  )
}

function VenueSections({ track }: { track: TracksShowTrack }) {
  const hasAny =
    track.parking_html ||
    track.pit_access_html ||
    track.connectivity_html ||
    track.transportation_html ||
    track.accessibility_html ||
    track.family_info_html ||
    track.prohibited_items_html ||
    track.rules ||
    track.venue_map_url

  if (!hasAny) return null

  return (
    <Box mt="8">
      <Heading size="7" mb="4">What to Expect</Heading>
      <Flex direction="column" gap="5">
        {track.venue_map_url && (
          <Box>
            <Heading size="4" mb="2">Venue Map</Heading>
            <img
              src={track.venue_map_url}
              alt={`${track.name} venue map`}
              style={{ maxWidth: "100%", borderRadius: 6 }}
            />
          </Box>
        )}
        {track.parking_html && <RichSection title="Parking" html={track.parking_html} />}
        {track.pit_access_html && <RichSection title="Pit Access" html={track.pit_access_html} />}
        {track.connectivity_html && <RichSection title="Connectivity" html={track.connectivity_html} />}
        {track.transportation_html && <RichSection title="Transportation" html={track.transportation_html} />}
        {track.accessibility_html && <RichSection title="Accessibility" html={track.accessibility_html} />}
        {track.family_info_html && <RichSection title="Family Info" html={track.family_info_html} />}
        {track.prohibited_items_html && <RichSection title="Prohibited Items" html={track.prohibited_items_html} />}
        {track.rules && (
          <Box>
            <Heading size="4" mb="2">Rules</Heading>
            <Text as="p" className="whitespace-pre-line">{track.rules}</Text>
          </Box>
        )}
      </Flex>
    </Box>
  )
}

/** One icon + text item in the header's meta row, linked when it goes somewhere. */
function MetaItem({ icon, href, children }: { icon: ReactNode; href?: string; children: ReactNode }) {
  const body = (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-gray9">{icon}</span>
      {children}
    </span>
  )
  return (
    <Text size="2" color="gray">
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent11 hover:underline">
          {body}
        </a>
      ) : (
        body
      )}
    </Text>
  )
}

function TrackHeader({ track, like }: Pick<TracksShowProps, "track" | "like">) {
  const address = [
    track.street,
    [[track.city, track.state].filter(Boolean).join(", "), track.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ")
  const directionsUrl =
    track.google_map_url ||
    `https://www.google.com/maps/search/?api=1&query=${track.latitude},${track.longitude}`
  const instagram = track.instagram?.replace(/^@+/, "")
  const hashtag = track.instagram_hashtag?.replace(/^#+/, "")

  return (
    <Flex direction="column" gap="4">
      <Breadcrumb crumbs={[{ label: "Tracks", href: "/tracks" }, { label: track.name }]} />
      <Heading size="8" className="text-xl md:text-2xl lg:text-4xl">
        {track.name}
      </Heading>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        {address && <MetaItem icon={<MapPin size={14} />}>{address}</MetaItem>}
        <MetaItem icon={<Navigation size={14} />} href={directionsUrl}>Directions</MetaItem>
        {track.website_url && (
          <MetaItem icon={<ExternalLink size={14} />} href={track.website_url}>Website</MetaItem>
        )}
        {instagram && (
          <MetaItem icon={<Instagram size={14} />} href={`https://instagram.com/${instagram}`}>
            @{instagram}
          </MetaItem>
        )}
        {hashtag && (
          <MetaItem icon={<Hash size={14} />} href={`https://instagram.com/explore/tags/${hashtag}`}>
            {hashtag}
          </MetaItem>
        )}
      </div>
      <div>
        <LikeButton likeable={track} type="Track" like={like} />
      </div>
    </Flex>
  )
}

export default function TrackShow({
  track,
  like,
  upcoming,
  past,
  drivers,
  media,
  report_reasons,
  sponsor_leaderboard,
  sponsor_mobile_banner,
}: TracksShowProps) {
  return (
    <>
      <Head title={track.name} />
      <Section size="1" px="4">
        <Container size="4">
          <TrackHeader track={track} like={like} />

          <Box className={SPONSOR_DESKTOP_ONLY} mt="6">
            <SponsorSlot {...sponsor_leaderboard} />
          </Box>
          <Box className={SPONSOR_MOBILE_ONLY} mt="6">
            <SponsorSlot {...sponsor_mobile_banner} />
          </Box>

          <Box mt="8">
            <EventFeed
              upcoming={upcoming}
              recent={past}
              showTrack={false}
              emptyMessage={`No events at ${track.name} yet.`}
            />
          </Box>

          {/* Everyone who's ever been — the event page's cards, across every past event here */}
          {media.length > 0 && (
            <Box mt="8">
              <Heading size="7" mb="4">
                Media who've shot here
              </Heading>
              <MediaBrowser groups={[{ key: "all", items: media }]} />
            </Box>
          )}

          {drivers.length > 0 && (
            <Box mt="8">
              <Heading size="7" mb="4">
                Drivers who've run here
              </Heading>
              <CarBrowser reportReasons={report_reasons} groups={[{ key: "all", items: drivers }]} />
            </Box>
          )}
          {/* What to expect at the venue */}
          <VenueSections track={track} />
        </Container>
      </Section>
    </>
  )
}
